import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

interface Company {
  id: number | string;
  company_name: string;
  address?: string;
  pancard?: string;
  city?: string;
  state?: string;
  country?: string;
  gst_number?: string;
  gst_uin?: string;
  code?: string;
  cin?: string;
  email?: string;
  HSN_SAC?: string;
  GST_SLAB?: string;
  signature?: string;
  [key: string]: any;
}

interface Customer {
  id: number | string;
  company_name: string;
  city?: string;
  state?: string;
  gst_number?: string;
  gst_uin?: string;
  [key: string]: any;
}

interface InvoiceDB {
  id?: number | string;
  Company_id: string | number;
  Customer_id: string | number;
  InvoiceNo: string;
  Date: string;
  Particulars: string;
  HSN_SAC: string | number;
  SGSTAmount: number;
  CGSTAmount: number;
  IGSTAmount: number;
  Total_Amount: number;
  GST_RATE?: string | number;
  paid?: boolean | number;
  amount_paid?: number;
  cancelled?: boolean | number;
  remarks?: string;
  Amounts?: string | string[];
  hsn_codes?: string | string[];
  shipToAddress?: string;
  created_at?: string;
  delete?: number;
}

function getInvoiceRowsFromDB(inv: InvoiceDB) {
  // ...unchanged...
  if (!inv) return [];
  let particularsArr: string[] = [];
  if (
    typeof inv.Particulars === "string" &&
    inv.Particulars.trim().startsWith("[") &&
    inv.Particulars.includes('"')
  ) {
    try {
      const arr = JSON.parse(inv.Particulars);
      if (Array.isArray(arr)) particularsArr = arr.map(String);
    } catch {}
  }
  if (!particularsArr.length && typeof inv.Particulars === "string") {
    particularsArr = [inv.Particulars];
  }
  let hsnArr: string[] = [];
  let _hsnRaw: any = (inv as any).hsn_codes !== undefined ? (inv as any).hsn_codes : inv.HSN_SAC;
  if (
    typeof _hsnRaw === "string" &&
    _hsnRaw.trim().startsWith("[") &&
    _hsnRaw.includes('"')
  ) {
    try {
      const arr = JSON.parse(_hsnRaw);
      if (Array.isArray(arr)) hsnArr = arr.map(String);
    } catch {}
  }
  if (!hsnArr.length && _hsnRaw) {
    hsnArr = [String(_hsnRaw)];
  }
  let amountArr: string[] = [];
  let _amtsRaw: any = (inv as any).Amounts;
  if (
    typeof _amtsRaw === "string" &&
    _amtsRaw.trim().startsWith("[") &&
    _amtsRaw.includes('"')
  ) {
    try {
      const arr = JSON.parse(_amtsRaw);
      if (Array.isArray(arr)) amountArr = arr.map(String);
    } catch {}
  } else if (
    typeof _amtsRaw === "string" &&
    _amtsRaw.trim().startsWith("[") &&
    _amtsRaw.includes(",")
  ) {
    try {
      const arr = JSON.parse(_amtsRaw);
      if (Array.isArray(arr)) amountArr = arr.map(String);
    } catch {}
  } else if (Array.isArray(_amtsRaw)) {
    amountArr = _amtsRaw.map(String);
  }
  if (!amountArr.length && inv.Total_Amount !== undefined) {
    amountArr = [
      String(
        Number(inv.Total_Amount) -
          Number(inv.CGSTAmount || 0) -
          Number(inv.SGSTAmount || 0) -
          Number(inv.IGSTAmount || 0)
      ),
    ];
  }
  let rows: { particulars: string; hsn_sac: string; amount: string }[] = [];
  if (
    particularsArr.length > 1 ||
    hsnArr.length > 1 ||
    amountArr.length > 1
  ) {
    const rowCount = Math.max(
      particularsArr.length,
      hsnArr.length,
      amountArr.length
    );
    for (let i = 0; i < rowCount; i++) {
      rows.push({
        particulars: particularsArr[i] ?? particularsArr[0] ?? "",
        hsn_sac: hsnArr[i] ?? hsnArr[0] ?? "",
        amount: amountArr[i] ?? amountArr[0] ?? "",
      });
    }
    rows.sort((a, b) => {
      if (a.hsn_sac < b.hsn_sac) return -1;
      if (a.hsn_sac > b.hsn_sac) return 1;
      return a.particulars.localeCompare(b.particulars);
    });
  }
  if (
    !rows.length &&
    typeof inv.Particulars === "string" &&
    inv.Particulars.startsWith("[{")
  ) {
    try {
      rows = JSON.parse(inv.Particulars);
      if (!Array.isArray(rows)) rows = [];
      rows = rows.map((r: any) => ({
        particulars: r.particulars ?? "",
        hsn_sac:
          typeof r.hsn_sac !== "undefined"
            ? r.hsn_sac
            : inv.HSN_SAC ?? "",
        amount:
          typeof r.amount !== "undefined"
            ? String(r.amount)
            : "0",
      }));
    } catch (e) {
      rows = [];
    }
  }
  if (!rows.length) {
    rows = [
      {
        particulars: particularsArr[0] ?? inv.Particulars ?? "",
        hsn_sac: hsnArr[0] ?? inv.HSN_SAC ?? "",
        amount: amountArr[0] ?? "0",
      },
    ];
  }
  return rows;
}

function numberToWords(num: number): string {
  // ...unchanged...
  if (isNaN(num)) return "";
  if (num === 0) return "Zero";
  const a = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const b = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const g = [
    "",
    "Thousand",
    "Lakh",
    "Crore",
    "Arab",
    "Kharab",
  ];
  let s = "";
  const n = Math.floor(num);

  function _inWordsHundred(n: number) {
    let str = "";
    if (n > 99) {
      str += a[Math.floor(n / 100)] + " Hundred ";
      n = n % 100;
    }
    if (n > 19) {
      str += b[Math.floor(n / 10)] + " " + a[n % 10];
    } else {
      str += a[n];
    }
    return str.trim();
  }

  let i = 0;
  let words = [];
  let segment;

  let remaining = n;
  if (remaining === 0) return "Zero";
  while (remaining > 0 && i < g.length) {
    if (i === 0) {
      segment = remaining % 1000;
      remaining = Math.floor(remaining / 1000);
    } else {
      segment = remaining % 100;
      remaining = Math.floor(remaining / 100);
    }
    if (segment)
      words.push(_inWordsHundred(segment) + (g[i] ? " " + g[i] : ""));
    i++;
  }

  s = words.reverse().join(" ").replace(/\s+/g, " ").trim();
  s = s.charAt(0).toUpperCase() + s.slice(1);
  return s;
}

function toCurrency(val: number | string) {
  const n = Number(val) || 0;
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2 });
}

function formatDate(dateString?: string) {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (String(d) === "Invalid Date") return dateString;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function formatDateToDDMMYYYY(dateString?: string) {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (String(d) === "Invalid Date") return dateString;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function getCompanySignatureUrl(company?: Company): string | undefined {
  if (!company?.signature) return undefined;
  const signature = company.signature.trim();
  if (signature.startsWith("http") || signature.startsWith("data:")) {
    return signature;
  }
  let cleanPath = signature.replace(/^\/+/, "");
  let apiBase = API_URL || "";
  if (apiBase.endsWith("/")) apiBase = apiBase.slice(0, -1);
  return `${apiBase}/${cleanPath}`;
}
function getSelectedGstRateFromInvoice(inv: InvoiceDB): number {
  // ...unchanged...
  let calculatedAmount = 0;
  if (
    typeof inv.Total_Amount === "number" &&
    typeof inv.SGSTAmount === "number" &&
    typeof inv.CGSTAmount === "number" &&
    typeof inv.IGSTAmount === "number"
  ) {
    calculatedAmount =
      inv.Total_Amount -
      inv.SGSTAmount -
      inv.CGSTAmount -
      inv.IGSTAmount;
  }
  let calculatedGstRate = "";
  if (
    typeof inv.Total_Amount === "number" &&
    typeof calculatedAmount === "number" &&
    calculatedAmount > 0
  ) {
    if ((inv.SGSTAmount ?? 0) > 0 && (inv.CGSTAmount ?? 0) > 0) {
      // Combined GST
      const rate =
        ((inv.SGSTAmount + inv.CGSTAmount) * 100) / calculatedAmount;
      calculatedGstRate = rate.toFixed(2);
    } else if ((inv.IGSTAmount ?? 0) > 0) {
      const rate = (inv.IGSTAmount * 100) / calculatedAmount;
      calculatedGstRate = rate.toFixed(2);
    }
  }

  // fallback to invoice GST_RATE
  return Number(calculatedGstRate);
}
function getTaxTableData(inv: InvoiceDB) {
  // ...unchanged...
  if (!inv) return { taxRows: [], totalTax: 0, uniqueHSN: [] };

  let hsnArr: string[] = [];
  let _hsnRaw: any = (inv as any).hsn_codes !== undefined ? (inv as any).hsn_codes : inv.HSN_SAC;
  if (
    typeof _hsnRaw === "string" &&
    _hsnRaw.trim().startsWith("[") &&
    _hsnRaw.includes('"')
  ) {
    try {
      const arr = JSON.parse(_hsnRaw);
      if (Array.isArray(arr)) hsnArr = arr.map(String);
    } catch {}
  }
  if (!hsnArr.length && _hsnRaw) {
    hsnArr = [String(_hsnRaw)];
  }
  const uniqueHSN: string[] = Array.from(
    new Set(hsnArr.map((v) => String(v).trim() || "-"))
  );
  const gstrate = getSelectedGstRateFromInvoice(inv);
  let amountArr: number[] = [];
  let _amtsRaw: any = (inv as any).Amounts;
  if (
    typeof _amtsRaw === "string" &&
    _amtsRaw.trim().startsWith("[") &&
    _amtsRaw.includes(",")
  ) {
    try {
      const arr = JSON.parse(_amtsRaw);
      if (Array.isArray(arr)) amountArr = arr.map(Number);
    } catch {}
  } else if (Array.isArray(_amtsRaw)) {
    amountArr = _amtsRaw.map(Number);
  }
  if (!amountArr.length && inv.Total_Amount !== undefined) {
    amountArr = [
      Number(inv.Total_Amount) -
        Number(inv.SGSTAmount || 0) -
        Number(inv.CGSTAmount || 0) -
        Number(inv.IGSTAmount || 0),
    ];
  }
  type TaxRow = {
    hsn: string;
    taxableValue: number;
    igstRate: number;
    igstAmount: number;
    sgstRate: number;
    sgstAmount: number;
    cgstRate: number;
    cgstAmount: number;
    totalTax: number;
  };

  let rowMap: { [hsn: string]: TaxRow } = {};
  if (hsnArr.length > 0 && amountArr.length > 0) {
    for (let i = 0; i < Math.max(hsnArr.length, amountArr.length); i++) {
      let hsn = hsnArr[i] ?? hsnArr[0] ?? "-";
      let amount = amountArr[i] ?? amountArr[0] ?? 0;
      let igstRate = gstrate && Number(inv.IGSTAmount) > 0 ? Number(gstrate) : 0;
      let sgstRate = gstrate && Number(inv.SGSTAmount) > 0 ? Number(gstrate) / 2 : 0;
      let cgstRate = gstrate && Number(inv.CGSTAmount) > 0 ? Number(gstrate) / 2 : 0;
      let igstAmount = igstRate ? (igstRate * amount) / 100 : 0;
      let sgstAmount = sgstRate ? (sgstRate * amount) / 100 : 0;
      let cgstAmount = cgstRate ? (cgstRate * amount) / 100 : 0;
      let totalTax = igstAmount + sgstAmount + cgstAmount;
      if (rowMap[hsn]) {
        rowMap[hsn].taxableValue += amount;
        rowMap[hsn].igstAmount += igstAmount;
        rowMap[hsn].sgstAmount += sgstAmount;
        rowMap[hsn].cgstAmount += cgstAmount;
        rowMap[hsn].totalTax += totalTax;
      } else {
        rowMap[hsn] = {
          hsn,
          taxableValue: amount,
          igstRate,
          igstAmount,
          sgstRate,
          sgstAmount,
          cgstRate,
          cgstAmount,
          totalTax,
        };
      }
    }
  } else {
    let hsn = String(inv.HSN_SAC).trim() || "-";
    let taxableValue =
      Number(inv.Total_Amount || 0) -
      Number(inv.SGSTAmount || 0) -
      Number(inv.CGSTAmount || 0) -
      Number(inv.IGSTAmount || 0);
    let totalTax =
      Number(inv.SGSTAmount || 0) +
      Number(inv.CGSTAmount || 0) +
      Number(inv.IGSTAmount || 0);

    const igstRate = gstrate;
    const sgstRate = gstrate / 2;
    rowMap[hsn] = {
      hsn,
      taxableValue,
      igstRate,
      igstAmount: Number(inv.IGSTAmount),
      sgstRate,
      sgstAmount: Number(inv.SGSTAmount),
      cgstRate: sgstRate,
      cgstAmount: Number(inv.CGSTAmount),
      totalTax,
    };
  }

  const result = Object.values(rowMap);

  const totalTax = result.reduce((sum, row) => sum + row.totalTax, 0);
  return {
    taxRows: result,
    totalTax,
    uniqueHSN,
  };
}

function handleSendReminder(invoice: InvoiceDB) {
  alert(`Send payment reminder for invoice #${invoice.InvoiceNo}`);
}
function handleSendEmail(invoice: InvoiceDB) {
  alert(`Send invoice by email for invoice #${invoice.InvoiceNo}`);
}
function handleSendWhatsapp(invoice: InvoiceDB) {
  alert(`Send invoice by WhatsApp for invoice #${invoice.InvoiceNo}`);
}
function handleEditInvoice(invoice: InvoiceDB) {
  window.location.href = `/editinvoice?id=${invoice.id}`;
}
async function handleCancelInvoice(
  invoice: InvoiceDB,
  fetchAllData: () => void,
  setErrorMsg: (msg: string) => void,
  setUpdateLoadingId: (id: number | string | null) => void
) {
  if (
    !window.confirm(
      "Are you sure you want to cancel this invoice? This action will mark it as cancelled."
    )
  )
    return;
  setUpdateLoadingId(invoice.id!);
  setErrorMsg("");
  try {
    await axios.put(`${API_URL}/auth/invoice/cancel/${invoice.id}`);
    fetchAllData();
  } catch (err) {
    setErrorMsg("Failed to cancel the invoice.");
  } finally {
    setUpdateLoadingId(null);
  }
}
function decodeToken(token: any) {
  if (!token) return {};
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return {};
  }
}

// Advanced filter hook state
const Invoice: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [allInvoices, setAllInvoices] = useState<InvoiceDB[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [viewingInvoice, setViewingInvoice] = useState<InvoiceDB | null>(null);
  const [viewingBankDetails, setViewingBankDetails] = useState<any | null>(null);

  const [updateLoadingId, setUpdateLoadingId] = useState<number | string | null>(
    null
  );
  const [deleteLoadingId, setDeleteLoadingId] = useState<
    number | string | null
  >(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [restoreLoadingId, setRestoreLoadingId] = useState<number | string | null>(null);
  const [bankMapModalOpen, setBankMapModalOpen] = useState(false);
  const [selectedInvoiceForMap, setSelectedInvoiceForMap] = useState<InvoiceDB | null>(null);
  const [selectedMapStatus, setSelectedMapStatus] = useState<number>(1);
  const [statementRows, setStatementRows] = useState<any[]>([]);
  const [selectedStatementRowId, setSelectedStatementRowId] = useState<string>("");
  const [tdsAmount, setTdsAmount] = useState<string>("0");

  // Share modal states
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareChannel, setShareChannel] = useState<'email' | 'sms' | 'whatsapp'>('email');
  const [shareRecipient, setShareRecipient] = useState('');
  const [shareSubject, setShareSubject] = useState('');
  const [shareMessageText, setShareMessageText] = useState('');
  const [shareInvoice, setShareInvoice] = useState<InvoiceDB | null>(null);
  const [shareSending, setShareSending] = useState(false);

  const triggerShare = (inv: InvoiceDB, channel: 'email' | 'sms' | 'whatsapp', isReminder: boolean = false) => {
    const cust = customers.find(c => String(c.id) === String(inv.Customer_id));
    const comp = companies.find(c => String(c.id) === String(inv.Company_id));
    
    const custName = cust ? cust.company_name : 'Customer';
    const compName = comp ? comp.company_name : 'Our Company';
    
    let recipient = '';
    if (channel === 'email') {
      recipient = cust && cust.email ? cust.email : '';
    } else {
      recipient = cust && cust.number ? String(cust.number) : (cust && cust.phone ? String(cust.phone) : '');
    }

    let subject = '';
    let message = '';

    if (isReminder) {
      subject = `Payment Reminder: Invoice ${inv.InvoiceNo}`;
      message = `Hello ${custName},\n\nThis is a friendly reminder that invoice ${inv.InvoiceNo} dated ${new Date(inv.Date).toLocaleDateString('en-GB')} for the amount of ₹ ${inv.Total_Amount} is unpaid.\n\nPlease arrange for the payment at your earliest convenience.\n\nRegards,\n${compName}`;
    } else {
      subject = `Invoice Notification: ${inv.InvoiceNo}`;
      message = `Hello ${custName},\n\nHere are the details for invoice ${inv.InvoiceNo} dated ${new Date(inv.Date).toLocaleDateString('en-GB')}:\nTotal Amount: ₹ ${inv.Total_Amount}\n\nThank you for your business.\n\nRegards,\n${compName}`;
    }

    if (channel === 'sms') {
      message = `Dear ${custName}, invoice ${inv.InvoiceNo} of ₹ ${inv.Total_Amount} is due. Please pay. - ${compName}`;
    } else if (channel === 'whatsapp') {
      message = `Hello *${custName}*,\n\nHere is your invoice detail:\n*Invoice No:* ${inv.InvoiceNo}\n*Date:* ${new Date(inv.Date).toLocaleDateString('en-GB')}\n*Total Amount:* ₹ ${inv.Total_Amount}\n\nThank you for your business!\n\nRegards,\n*${compName}*`;
    }

    setShareInvoice(inv);
    setShareChannel(channel);
    setShareRecipient(recipient);
    setShareSubject(subject);
    setShareMessageText(message);
    setShareModalOpen(true);
  };

  const handleSendReminder = (invoice: InvoiceDB) => {
    triggerShare(invoice, 'email', true);
  };
  const handleSendEmail = (invoice: InvoiceDB) => {
    triggerShare(invoice, 'email', false);
  };
  const handleSendWhatsapp = (invoice: InvoiceDB) => {
    triggerShare(invoice, 'whatsapp', false);
  };

  const handleExecuteShare = async () => {
    if (!shareRecipient.trim() || !shareMessageText.trim()) {
      alert('Recipient and message text are required.');
      return;
    }
    setShareSending(true);
    try {
      await axios.post(`${API_URL}/auth/share`, {
        channel: shareChannel,
        recipient: shareRecipient,
        subject: shareSubject,
        messageText: shareMessageText,
        company_id: shareInvoice ? shareInvoice.Company_id : null
      });
      alert('Message shared successfully!');
      setShareModalOpen(false);
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Failed to share message.');
    } finally {
      setShareSending(false);
    }
  };

  // -- ADVANCED FILTER STATE --
  const [filter, setFilter] = useState<{
    invoiceNo: string;
    startDate: string;
    endDate: string;
    companyId: string;
    customerId: string;
    paid: string;
    cancelled: string;
    minAmount: string;
    maxAmount: string;
    search: string;
  }>({
    invoiceNo: "",
    startDate: "",
    endDate: "",
    companyId: "",
    customerId: "",
    paid: "",
    cancelled: "",
    minAmount: "",
    maxAmount: "",
    search: "",
  });

  // Store company signature URLs mapped by company id for easy use in UI
  const [companySignatureUrls, setCompanySignatureUrls] = useState<{
    [key: string]: string;
  }>({});

  // Store user's company id (if role = company)
  const [userCompanyId, setUserCompanyId] = useState<string | number | null>(
    null
  );
  const [role, setRole] = useState<string | null>(null);

  useEffect(() => {
    // Set user role and company id based on token/role
    const token = localStorage.getItem("token");
    const storedRole = localStorage.getItem("role");
    let decoded: any = null;
    if (token) {
      decoded = decodeToken(token);
    }
    setRole(storedRole || null);

    if (decoded) {
      const companyId =
        decoded.userId ||
        decoded.id ||
        decoded.companyId ||
        decoded.company_id ||
        decoded.cid;
      setUserCompanyId(companyId ? String(companyId) : null);
    } else {
      setUserCompanyId(null);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line
  }, []);

  const handlePrint = () => {
    const invoicePrintElem = document.getElementById("invoice-print");
    if (!invoicePrintElem) return;
    const printContents = invoicePrintElem.innerHTML;
    const printWindow = window.open("", "", "width=900,height=700");
    if (printWindow) {
      printWindow.document.write(`
        <html>
        <head>
          <title>Invoice</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 4px 7px !important; background: #f7fafb; color: #222; font-size: 11.5px !important; margin: 0; line-height: 1.25; max-width: 830px; width: 100vw; box-sizing: border-box; }
            .text-xs { font-size: 10px !important; }
            .font-semibold { font-weight: 600; }
            .font-bold { font-weight: 700; }
            .font-extrabold { font-weight: 800; }
            .text-lg { font-size: 14px !important; }
            .text-xl { font-size: 15px !important; }
            .text-green-700 { color: #15803d; }
            .text-blue-900 { color: #1e3a8a;}
            .text-blue-800 { color: #1e40af;}
            .text-blue-700 { color: #1d4ed8;}
            .text-gray-700 { color: #374151;}
            .text-gray-800 { color: #1f2937;}
            .italic { font-style: italic; }
            .bg-blue-50 { background: #eff6ff; }
            .bg-blue-100 { background: #dbeafe; }
            .bg-gradient-to-tr {
              background: linear-gradient(135deg, #fff 0%, #eaeffe 40%, #f0f7ff 100%);
            }
            .bg-gradient-to-t { background: linear-gradient(0deg, #eaeffe 0%, #e8f5fe 100%);}
            .bg-gradient-to-l { background: linear-gradient(270deg, #ffe4ef 0%, #fff 100%);}
            .bg-gray-50 { background: #f9fafb;}
            .bg-white { background: #fff;}
            .mb-1 { margin-bottom: 2px !important;}
            .ml-8 { margin-left: 16px !important;}
            .mb-2 { margin-bottom: 6px !important;}
            .mb-4 { margin-bottom: 10px !important;}
            .mb-5 { margin-bottom: 11px !important;}
            .mb-6 { margin-bottom: 13px !important;}
            .mt-1 { margin-top: 2px !important;}
            .mt-2 { margin-top: 4px !important;}
            .mt-4 { margin-top: 9px !important;}
            .mt-5 { margin-top: 10px !important;}
            .rounded-2xl { border-radius: 0.65rem;}
            .rounded-xl { border-radius: 0.4rem;}
            .rounded-lg { border-radius: 0.25rem;}
            .rounded-bl-lg { border-bottom-left-radius: 0.25rem;}
            .rounded-br-lg { border-bottom-right-radius: 0.25rem;}
            .border { border: 1px solid #cbd5e1;}
            .border-blue-100 { border-color: #dbeafe;}
            .border-blue-200 { border-color: #bfdbfe;}
            .border-blue-300 { border-color: #93c5fd;}
            .border-gray-200 { border-color: #e5e7eb;}
            .shadow, .shadow-lg, .shadow-2xl, .shadow-xl { box-shadow: none !important;}
            .table-title { background: #2062b8; color: #fff; font-weight: 600;}
            .invoice-total { background: #e9fbe8 !important; font-size: 13px !important;}
            .w-full { width: 100%;}
            .w-1\\/2 { width: 50%; }
            .align-top { vertical-align: top;}
            .text-right { text-align: right;}
            .text-center { text-align: center;}
            .flex { display: flex;}
            .flex-col { flex-direction: column;}
            .flex-row { flex-direction: row;}
            .items-center { align-items: center;}
            .items-end { align-items: flex-end;}
            .text-end { text-align: end;}
            .justify-between { justify-content: space-between;}
            .justify-end { justify-content: flex-end;}
            .justify-center { justify-content: center;}
            .gap-5 { gap: 10px;}
            .gap-6 { gap: 15px;}
            .overflow-auto { overflow: auto;}
            .overflow-x-auto { overflow-x: auto;}
            .h-12 { height: 36px !important;}
            .p-2 {padding:5px!important;} 
            .p-3 { padding: 7px !important;}
            .p-4 { padding: 7px !important;}
            .p-6 { padding: 14px !important;}
            .py-1 { padding-top: 2px !important; padding-bottom: 2px !important;}
            .py-2 { padding-top: 5px !important; padding-bottom: 5px !important;}
            .py-3 { padding-top: 7px !important; padding-bottom: 7px !important;}
            .px-2 { padding-left: 5px !important; padding-right: 5px !important;}
            .px-3 { padding-left: 8px !important; padding-right: 8px !important;}
            .px-6 { padding-left: 14px !important; padding-right: 14px !important;}
            .border-collapse { border-collapse: collapse;}
            .border-l-0 { border-left: 0;}
            .border-r { border-right: 1px solid #bfdbfe;}
            #invoice-print { min-height: 100%; overflow: visible; }
            /* Added styling for Tax Invoice heading */
            .tax-invoice-heading {
              text-align: center;
              font-size: 22px;
              font-weight: 800;
              letter-spacing: 1px;
              color: #1e3a8a;
              padding-top: 24px;
              padding-bottom: 18px;
            }
            @media print {
              html, body {
                width: 100vw;
                max-width: 856px !important;
                margin: 0 auto !important;
                padding: 0 !important;
                overflow: visible !important;
                font-size: 11.5px !important;
              }
              #invoice-print {
                padding: 0 6px !important;
                margin: 0 !important;
                width: 100% !important;
                max-width: 848px !important;
                max-height: none !important;
                overflow: visible !important;
              }
              .shadow, .shadow-lg, .shadow-xl, .shadow-2xl { box-shadow: none !important;}
              .bg-gradient-to-tr, .bg-gradient-to-t, .bg-gradient-to-l, .bg-gray-50, .bg-blue-50, .bg-blue-100, .bg-white { background: #fff !important; }
              tr, td, th, table, div, span, section {
                page-break-inside: avoid !important;
                page-break-after: auto !important;
                page-break-before: auto !important;
              }
              table, tbody, thead, tfoot, tr, td, th {
                margin: 0 !important;
                padding: 0 !important;
              }
              .tax-invoice-heading {
                text-align: center;
                font-size: 22px;
                font-weight: 800;
                letter-spacing: 1px;
                color: #1e3a8a;
                padding-top: 24px;
                padding-bottom: 18px;
              }
            }
          </style>
        </head>
        <body>
          ${printContents.replace(
            /^(\s*)<div[^>]*id="?invoice-print"?[^>]*>/i,
            ""
          ).replace(/<\/div>\s*$/, "")}
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
        }, 350);
      };
    }
  };

  function fetchAllData() {
    setLoading(true);

    Promise.all([
      axios.get(`${API_URL}/auth/companies`),
      axios.get(`${API_URL}/auth/customers`),
      axios.get(`${API_URL}/auth/invoices?include_deleted=true`),
      axios.get(`${API_URL}/auth/bank-statement-rows`).catch(() => ({ data: [] })),
    ])
      .then(([comp, cust, invs, bsRows]) => {
        const compArray: Company[] = Array.isArray(comp.data) ? comp.data : [];
        setCompanies(compArray);
        setCustomers(Array.isArray(cust.data) ? cust.data : []);
        setStatementRows(Array.isArray(bsRows.data) ? bsRows.data : []);

        // ------------ FILTRATION LOGIC ---------------
        let filteredInvoices: InvoiceDB[] = [];
        let currentRole = localStorage.getItem("role");
        let companyId: any = null;
        const token = localStorage.getItem("token");
        if (token && currentRole === "company") {
          const decoded = decodeToken(token);
          companyId =
            decoded.userId ||
            decoded.id ||
            decoded.companyId ||
            decoded.company_id ||
            decoded.cid;
        }

        const invoiceArray: InvoiceDB[] = Array.isArray(invs.data)
          ? invs.data
          : [];

        if (currentRole === "company" && companyId) {
          // Only invoices with this company id
          filteredInvoices = invoiceArray.filter(
            (inv) => String(inv.Company_id) === String(companyId)
          );
        } else {
          // If role not found, show all
          filteredInvoices = invoiceArray;
        }

        setAllInvoices(filteredInvoices);

        // Fill signature URLs for all companies and store to state
        const signatureMap: { [key: string]: string } = {};
        compArray.forEach((c) => {
          const url = getCompanySignatureUrl(c);
          if (url) signatureMap[String(c.id)] = url;
        });
        setCompanySignatureUrls(signatureMap);
      })
      .catch(() => setErrorMsg("Failed to fetch data"))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (viewingInvoice && viewingInvoice.Company_id) {
      axios.get(`${API_URL}/auth/company/${viewingInvoice.Company_id}/bank`)
        .then((res) => {
          const banks = Array.isArray(res.data) ? res.data : [];
          setViewingBankDetails(banks.length > 0 ? banks[0] : null);
        })
        .catch(() => setViewingBankDetails(null));
    } else {
      setViewingBankDetails(null);
    }
  }, [viewingInvoice]);

  function handleViewModal(inv: InvoiceDB) {
    setViewingInvoice(inv);
  }

  function handleCloseModal() {
    setViewingInvoice(null);
  }

  async function handlePaidStatusUpdate(invId: number | string, paidStatus: number, amountPaid?: number, paymentMode?: string) {
    setUpdateLoadingId(invId);
    setErrorMsg("");
    try {
      await axios.put(`${API_URL}/auth/invoiceremark/${invId}`, {
        Paid: paidStatus,
        amount_paid: amountPaid,
        payment_mode: paymentMode
      });
      await fetchAllData();
    } catch (err) {
      setErrorMsg("Failed to update Paid status.");
    } finally {
      setUpdateLoadingId(null);
    }
  }

  async function handleDeleteInvoice(invId: number | string) {
    if (
      !window.confirm(
        "Are you sure you want to delete this invoice? This action cannot be undone."
      )
    )
      return;

    setDeleteLoadingId(invId);
    setErrorMsg("");
    try {
      await axios.delete(`${API_URL}/auth/invoice/${invId}`);
      await fetchAllData();
    } catch (err) {
      setErrorMsg("Failed to delete the invoice.");
    } finally {
      setDeleteLoadingId(null);
    }
  }

  async function handleRestoreInvoice(invId: number | string) {
    if (!window.confirm("Are you sure you want to restore this invoice?")) return;
    setRestoreLoadingId(invId);
    setErrorMsg("");
    try {
      await axios.put(`${API_URL}/auth/invoice/${invId}/restore`);
      await fetchAllData();
    } catch (err) {
      console.error(err);
      setErrorMsg("Failed to restore the invoice.");
    } finally {
      setRestoreLoadingId(null);
    }
  }

  function getPreviewRows() {
    if (!viewingInvoice) return [];
    return getInvoiceRowsFromDB(viewingInvoice);
  }

  function getInvoicePreviewData() {
    if (!viewingInvoice) {
      return {
        selectedGstRate: 0,
        cgstAmount: 0,
        sgstAmount: 0,
        igstAmount: 0,
        totalAmount: 0,
        companyId: "",
      };
    }
    const inv = viewingInvoice;
    const selectedGstRate = getSelectedGstRateFromInvoice(inv);
    return {
      selectedGstRate,
      cgstAmount: Number(inv.CGSTAmount || 0),
      sgstAmount: Number(inv.SGSTAmount || 0),
      igstAmount: Number(inv.IGSTAmount || 0),
      totalAmount: Number(inv.Total_Amount || 0),
      companyId: inv.Company_id,
    };
  }

  // ---- Advanced Filter Logic ----
  function filterInvoicesForTable(invoices: InvoiceDB[]) {
    return invoices.filter((inv) => {
      // Filter by deleted status
      if (showDeleted) {
        if (Number(inv.delete) !== 1) return false;
      } else {
        if (Number(inv.delete) === 1) return false;
      }

      // InvoiceNo filter (partial match)
      if (
        filter.invoiceNo &&
        !String(inv.InvoiceNo).toLowerCase().includes(filter.invoiceNo.toLowerCase())
      )
        return false;

      // Date Filters
      if (filter.startDate) {
        const invDate = new Date(inv.Date);
        if (String(invDate) === "Invalid Date") return false;
        if (invDate < new Date(filter.startDate)) return false;
      }
      if (filter.endDate) {
        const invDate = new Date(inv.Date);
        if (String(invDate) === "Invalid Date") return false;
        if (invDate > new Date(filter.endDate)) return false;
      }

      // Company filter
      if (filter.companyId && String(inv.Company_id) !== filter.companyId) return false;

      // Customer filter
      if (filter.customerId && String(inv.Customer_id) !== filter.customerId) return false;

      // Status Filter (includes Paid, Unpaid, Partially Paid, and Cancelled)
      if (filter.paid) {
        if (filter.paid === "cancelled") {
          if (!(!!inv.cancelled || Number(inv.cancelled) === 1)) return false;
        } else {
          // If filtering by regular payment status, exclude cancelled ones
          if (!!inv.cancelled || Number(inv.cancelled) === 1) return false;
          if (filter.paid === "paid" && Number(inv.paid) !== 1) return false;
          if (filter.paid === "unpaid" && Number(inv.paid) !== 0) return false;
          if (filter.paid === "partial" && Number(inv.paid) !== 2) return false;
        }
      }

      // Amount Range
      const amt = Number(inv.Total_Amount || 0);
      if (filter.minAmount && amt < Number(filter.minAmount)) return false;
      if (filter.maxAmount && amt > Number(filter.maxAmount)) return false;

      // Global text search
      if (filter.search) {
        const search = filter.search.toLowerCase();
        let match =
          String(inv.InvoiceNo).toLowerCase().includes(search) ||
          String(inv.Date).toLowerCase().includes(search) ||
          String(inv.Total_Amount).toLowerCase().includes(search);
        // Company name
        const company = companies.find((c) => String(c.id) === String(inv.Company_id));
        if (company?.company_name && company.company_name.toLowerCase().includes(search))
          match = true;
        // Customer name
        const customer = customers.find((c) => String(c.id) === String(inv.Customer_id));
        if (customer?.company_name && customer.company_name.toLowerCase().includes(search))
          match = true;
        if (!match) return false;
      }

      return true;
    });
  }

  const filteredInvoicesList = useMemo(() => {
    return filterInvoicesForTable(allInvoices);
  }, [allInvoices, filter, showDeleted, companies, customers]);

  const taxTableData = getTaxTableData(viewingInvoice || ({} as InvoiceDB));
  const invoicePreviewData = getInvoicePreviewData();

  const amountInWords = numberToWords(Number(invoicePreviewData.totalAmount));
  const taxAmountInWords = numberToWords(
    Number(invoicePreviewData.cgstAmount) +
      Number(invoicePreviewData.sgstAmount) +
      Number(invoicePreviewData.igstAmount)
  );

  // ---- JSX ----
  return (
    <div className="min-h-full flex flex-col bg-gradient-to-b from-blue-50 via-white to-gray-100">
      {/* Header section */}
      <div className="flex justify-between items-center gap-4 mt-6 mb-8 px-2 md:px-4">
        <div>
          <h2 className="text-3xl font-extrabold text-blue-800 text-center md:text-left tracking-tight">
            All Invoices
          </h2>
          <p className="text-left text-gray-500 mt-1 mb-0 text-md">
            Browse, view, update paid status, or delete invoices.
          </p>
        </div>
        <div>
          <button
            className="bg-gradient-to-r from-blue-700 to-blue-500 text-white hover:from-blue-500 hover:to-blue-400 px-6 py-2 rounded-full text-md font-semibold shadow transition duration-150"
            onClick={() => (window.location.href = "/InvoiceCreator")}
          >
            Create Invoice
          </button>
        </div>
      </div>

      {/* Advanced Filter Area */}
      <div className="mb-5 px-2 md:px-4">
        <div className="bg-white border border-blue-200 rounded-2xl p-6 shadow-md transition-all">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 items-end">
            <div className="flex flex-col">
              <label className="block text-xs mb-1.5 font-semibold text-blue-900/80 uppercase tracking-wider">Invoice No</label>
              <input
                className="w-full border-2 border-blue-50/50 rounded-xl px-3 py-2 text-sm bg-blue-50/20 focus:border-blue-300 focus:outline-none text-gray-800 font-medium transition"
                type="text"
                value={filter.invoiceNo}
                onChange={e => setFilter(f => ({ ...f, invoiceNo: e.target.value }))}
                placeholder="Invoice no, partial ok"
              />
            </div>
            <div className="flex flex-col">
              <label className="block text-xs mb-1.5 font-semibold text-blue-900/80 uppercase tracking-wider">Start Date</label>
              <input
                className="w-full border-2 border-blue-50/50 rounded-xl px-3 py-2 text-sm bg-blue-50/20 focus:border-blue-300 focus:outline-none text-gray-800 font-medium transition"
                type="date"
                value={filter.startDate}
                onChange={e => setFilter(f => ({ ...f, startDate: e.target.value }))}
              />
            </div>
            <div className="flex flex-col">
              <label className="block text-xs mb-1.5 font-semibold text-blue-900/80 uppercase tracking-wider">End Date</label>
              <input
                className="w-full border-2 border-blue-50/50 rounded-xl px-3 py-2 text-sm bg-blue-50/20 focus:border-blue-300 focus:outline-none text-gray-800 font-medium transition"
                type="date"
                value={filter.endDate}
                onChange={e => setFilter(f => ({ ...f, endDate: e.target.value }))}
              />
            </div>
            <div className="flex flex-col">
              <label className="block text-xs mb-1.5 font-semibold text-blue-900/80 uppercase tracking-wider">Company</label>
              <select
                className="w-full border-2 border-blue-50/50 rounded-xl px-3 py-2 text-sm bg-blue-50/20 focus:border-blue-300 focus:outline-none text-gray-800 font-medium transition"
                value={filter.companyId}
                onChange={e => setFilter(f => ({ ...f, companyId: e.target.value }))}
              >
                <option value="">All</option>
                {companies.map(c => (
                  <option value={c.id} key={c.id}>
                    {c.company_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label className="block text-xs mb-1.5 font-semibold text-blue-900/80 uppercase tracking-wider">Customer</label>
              <select
                className="w-full border-2 border-blue-50/50 rounded-xl px-3 py-2 text-sm bg-blue-50/20 focus:border-blue-300 focus:outline-none text-gray-800 font-medium transition"
                value={filter.customerId}
                onChange={e => setFilter(f => ({ ...f, customerId: e.target.value }))}
              >
                <option value="">All</option>
                {customers.map(c => (
                  <option value={c.id} key={c.id}>
                    {c.company_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label className="block text-xs mb-1.5 font-semibold text-blue-900/80 uppercase tracking-wider">Status</label>
              <select
                className="w-full border-2 border-blue-50/50 rounded-xl px-3 py-2 text-sm bg-blue-50/20 focus:border-blue-300 focus:outline-none text-gray-800 font-medium transition"
                value={filter.paid}
                onChange={e => setFilter(f => ({ ...f, paid: e.target.value }))}
              >
                <option value="">All</option>
                <option value="unpaid">Unpaid</option>
                <option value="partial">Partially Paid</option>
                <option value="paid">Paid</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="flex flex-col">
              <label className="block text-xs mb-1.5 font-semibold text-blue-900/80 uppercase tracking-wider">Amount Min</label>
              <input
                className="w-full border-2 border-blue-50/50 rounded-xl px-3 py-2 text-sm bg-blue-50/20 focus:border-blue-300 focus:outline-none text-gray-800 font-medium transition"
                type="number"
                value={filter.minAmount}
                onChange={e => setFilter(f => ({ ...f, minAmount: e.target.value }))}
                placeholder="₹ Min"
              />
            </div>
            <div className="flex flex-col">
              <label className="block text-xs mb-1.5 font-semibold text-blue-900/80 uppercase tracking-wider">Amount Max</label>
              <input
                className="w-full border-2 border-blue-50/50 rounded-xl px-3 py-2 text-sm bg-blue-50/20 focus:border-blue-300 focus:outline-none text-gray-800 font-medium transition"
                type="number"
                value={filter.maxAmount}
                onChange={e => setFilter(f => ({ ...f, maxAmount: e.target.value }))}
                placeholder="₹ Max"
              />
            </div>
            <div className="flex flex-col lg:col-span-2">
              <label className="block text-xs mb-1.5 font-semibold text-blue-900/80 uppercase tracking-wider">Search Text</label>
              <input
                className="w-full border-2 border-blue-50/50 rounded-xl px-3 py-2 text-sm bg-blue-50/20 focus:border-blue-300 focus:outline-none text-gray-800 font-medium transition"
                type="text"
                value={filter.search}
                onChange={e => setFilter(f => ({ ...f, search: e.target.value }))}
                placeholder="Invoice, customer, company ..."
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between border-t border-gray-100 pt-4 mt-5">
            <label className="flex items-center gap-2 text-sm font-semibold text-red-600 hover:text-red-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={e => setShowDeleted(e.target.checked)}
                className="rounded text-red-600 focus:ring-red-500 w-4 h-4 border-gray-300 transition"
              />
              Show Deleted Invoices
            </label>
            <button
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm px-6 py-2 rounded-xl shadow transition duration-150 flex items-center gap-1.5"
              onClick={() => setFilter({
                invoiceNo: "",
                startDate: "",
                endDate: "",
                companyId: "",
                customerId: "",
                paid: "",
                cancelled: "",
                minAmount: "",
                maxAmount: "",
                search: "",
              })}
              type="button"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {errorMsg && (
        <div className="mb-4 p-2 rounded border text-center shadow bg-red-50 text-red-700 border-red-200 mx-2 md:mx-4">
          {errorMsg}
        </div>
      )}

      {/* Main Table Area - scrollable if many invoices */}
      <div className="flex-1 px-2 md:px-4 pb-6">
        <div className="rounded-3xl  bg-gradient-to-br from-white via-blue-50 to-slate-50 shadow-2xl border border-blue-200 p-6">
          <table className=" min-w-[920px] w-full text-sm rounded-xl shadow  border-separate border-spacing-y-2 ">
            <thead>
              <tr className="bg-gradient-to-l from-blue-600 to-blue-400 text-white text-base rounded-xl">
                <th className="px-3 py-2 rounded-l-xl">#</th>
                <th className="px-3 py-2">Invoice No</th>
                <th className="px-3 py-2">Invoice Date</th>
                <th className="px-3 py-2">Created Date</th>
                <th className="px-3 py-2">Company</th>
                <th className="px-3 py-2">Buyer</th>
                <th className="px-3 py-2">Amount</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 rounded-r-xl">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={9}
                    className="py-10 text-center text-gray-400 text-lg"
                  >
                    Loading...
                  </td>
                </tr>
              ) : filteredInvoicesList.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    className="py-10 text-center text-gray-400 text-lg"
                  >
                    No invoices found.
                  </td>
                </tr>
              ) : (
                filteredInvoicesList.map((inv, idx) => {
                  const company = companies.find(
                    (c) => String(c.id) === String(inv.Company_id)
                  );
                  const customer = customers.find(
                    (c) => String(c.id) === String(inv.Customer_id)
                  );
                  return (
                    <tr
                      key={inv.id}
                      className="transition-all hover:ring-4 hover:ring-blue-200/30 hover:shadow-lg rounded-lg even:bg-slate-50"
                    >
                      <td className="py-1 px-3 font-medium text-blue-700 select-none">
                        {idx + 1}
                      </td>
                      <td className="py-1 px-3 font-semibold">
                        <div>
                          <span>{inv.InvoiceNo}</span>
                          {(inv as any).invoice_type === 'lut' && (
                            <span className="ml-1.5 text-[9px] text-amber-800 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5 font-bold uppercase">LUT</span>
                          )}
                          {(inv as any).invoice_type === 'commission' && (
                            <span className="ml-1.5 text-[9px] text-purple-800 bg-purple-100 border border-purple-200 rounded px-1.5 py-0.5 font-bold uppercase">Commission</span>
                          )}
                          {(inv as any).invoice_type === 'proforma' && (
                            <span className="ml-1.5 text-[9px] text-sky-800 bg-sky-100 border border-sky-200 rounded px-1.5 py-0.5 font-bold uppercase">Proforma</span>
                          )}
                          {(inv as any).verified_recurring === 1 && (
                            <div className="text-[10px] text-orange-600 bg-orange-50 border border-orange-100 rounded px-1.5 py-0.5 mt-0.5 inline-block font-sans">
                              ⚠️ Unverified (Please Edit to Renumber)
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-1 px-3">{formatDate(inv.Date)}</td>
                      <td className="py-1 px-3 text-xs text-gray-500 font-sans">
                        {inv.created_at ? formatDate(inv.created_at) : "-"}
                      </td>
                      <td className="py-1 px-3">
                        {company?.company_name || "-"}
                      </td>
                      <td className="py-1 px-3">
                        {customer?.company_name || "-"}
                      </td>
                      <td className="py-1 px-3">
                        ₹ {toCurrency(inv.Total_Amount)}
                      </td>
                      <td className="py-1 px-3 text-center">
                        <div className="flex flex-col items-center justify-center gap-1">
                              <select
                                value={
                                  !!inv.cancelled || Number(inv.cancelled) === 1
                                    ? "cancelled"
                                    : Number(inv.paid) === 1
                                    ? `1_${(inv as any).payment_mode || 'bank'}`
                                    : Number(inv.paid) === 2
                                    ? `2_${(inv as any).payment_mode || 'bank'}`
                                    : "0"
                                }
                                disabled={updateLoadingId === inv.id}
                                onChange={async (e) => {
                                  const val = e.target.value;
                                  if (val === "cancelled") {
                                    await handleCancelInvoice(
                                      inv,
                                      fetchAllData,
                                      setErrorMsg,
                                      setUpdateLoadingId
                                    );
                                  } else if (val === "0") {
                                    await handlePaidStatusUpdate(inv.id!, 0, 0, 'bank');
                                  } else {
                                    const [statusStr, mode] = val.split('_');
                                    const status = Number(statusStr);
                                    if (mode === 'bank') {
                                      setSelectedInvoiceForMap(inv);
                                      setSelectedMapStatus(status);
                                      setSelectedStatementRowId("");
                                      setTdsAmount("0");
                                      setBankMapModalOpen(true);
                                    } else {
                                      if (status === 2) {
                                        const amtStr = window.prompt("Enter the amount paid (₹):", String(inv.amount_paid || ""));
                                        if (amtStr !== null) {
                                          const amt = parseFloat(amtStr);
                                          if (!isNaN(amt) && amt >= 0) {
                                            await handlePaidStatusUpdate(inv.id!, 2, amt, mode);
                                          } else {
                                            alert("Invalid amount entered.");
                                          }
                                        }
                                      } else {
                                        await handlePaidStatusUpdate(inv.id!, status, undefined, mode);
                                      }
                                    }
                                  }
                                }}
                                className={`px-2 py-0.5 rounded-full text-xs font-bold border cursor-pointer focus:outline-none transition-colors ${
                                  !!inv.cancelled
                                    ? "bg-gray-100 text-gray-800 border-gray-200"
                                    : Number(inv.paid) === 1
                                      ? "bg-green-100 text-green-800 border-green-200 hover:bg-green-200"
                                      : Number(inv.paid) === 2
                                        ? "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200"
                                        : "bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200"
                                }`}
                              >
                                <option value="0">Unpaid</option>
                                <option value="2_bank">Partially Paid (Bank)</option>
                                <option value="2_cash">Partially Paid (Cash)</option>
                                <option value="1_bank">Paid (Bank)</option>
                                <option value="1_cash">Paid (Cash)</option>
                                <option value="cancelled">Cancelled</option>
                              </select>
                              {Number(inv.paid) === 2 && Number(inv.amount_paid) > 0 && (
                                <span className="text-[10px] font-semibold text-blue-700 mt-0.5">
                                  Paid: ₹{Number(inv.amount_paid).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </span>
                              )}
                              {Number(inv.paid) > 0 && (inv as any).payment_mode && (
                                <span className="text-[9px] uppercase font-bold text-gray-500 font-sans tracking-wider mt-0.5">
                                  ({(inv as any).payment_mode})
                                </span>
                              )}
                              {(() => {
                                const linked = statementRows.filter(r => 
                                  (r.mapped_invoice_id && Number(r.mapped_invoice_id) === Number(inv.id)) ||
                                  (r.mapped_invoice_ids && String(r.mapped_invoice_ids).split(',').map(id => id.trim()).includes(String(inv.id)))
                                );
                                if (linked.length > 0) {
                                  return (
                                    <span 
                                      className="text-[9px] font-bold text-green-700 bg-green-50 border border-green-200 rounded px-1.5 py-0.5 mt-0.5 inline-block select-none" 
                                      title={linked.map(l => `${l.date_str ? l.date_str.substring(0,10) : ''}: ₹${l.credit}`).join('\n')}
                                    >
                                      🔗 {linked.length} trx linked
                                    </span>
                                  );
                                }
                                return null;
                              })()}
                              {updateLoadingId === inv.id && (
                                <span className="ml-2 text-blue-500 text-xs animate-pulse">
                                  Updating...
                                </span>
                              )}
                        </div>
                      </td>
                      <td className="py-1 px-3 text-center">
                        <div className="flex flex-wrap gap-1.5 justify-center items-center min-w-[280px]">
                          {Number(inv.delete) === 1 ? (
                          <>
                            <button
                              className="px-3 py-1.5 bg-gradient-to-t from-blue-500 to-blue-300 rounded-lg text-white text-xs font-bold hover:from-blue-700 hover:to-blue-500 shadow transition"
                              title="View Invoice"
                              onClick={() => handleViewModal(inv)}
                            >
                              View
                            </button>
                            <a
                              href={`${API_URL}/uploads/invoice_${inv.id}.pdf`}
                              target="_blank"
                              rel="noreferrer"
                              className="px-3 py-1.5 bg-gradient-to-t from-red-500 to-red-400 rounded-lg text-white text-xs font-bold hover:from-red-700 hover:to-red-500 shadow transition flex items-center justify-center"
                              title="View PDF"
                            >
                              PDF
                            </a>
                            <button
                              className="px-3 py-1.5 bg-gradient-to-t from-green-600 to-green-400 rounded-lg text-white text-xs font-bold hover:from-green-800 hover:to-green-600 shadow transition"
                              title="Restore Invoice"
                              onClick={() => handleRestoreInvoice(inv.id!)}
                              disabled={restoreLoadingId === inv.id}
                            >
                              {restoreLoadingId === inv.id ? "Restoring..." : "Restore"}
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="px-3 py-1.5 bg-gradient-to-t from-blue-500 to-blue-300 rounded-lg text-white text-xs font-bold hover:from-blue-700 hover:to-blue-500 shadow transition"
                              title="View Invoice"
                              onClick={() => handleViewModal(inv)}
                            >
                              View
                            </button>
                            <a
                              href={`${API_URL}/uploads/invoice_${inv.id}.pdf`}
                              target="_blank"
                              rel="noreferrer"
                              className="px-3 py-1.5 bg-gradient-to-t from-red-500 to-red-400 rounded-lg text-white text-xs font-bold hover:from-red-700 hover:to-red-500 shadow transition flex items-center justify-center"
                              title="View PDF"
                            >
                              PDF
                            </a>
                            <button
                              className="px-3 py-1.5 bg-gradient-to-t from-orange-400 to-orange-300 rounded-lg text-white text-xs font-bold hover:from-orange-700 hover:to-orange-500 shadow transition"
                              title="Edit Invoice"
                              onClick={() => handleEditInvoice(inv)}
                              disabled={!!inv.cancelled}
                            >
                              Edit
                            </button>
                            <button
                              className="px-3 py-1.5 bg-gradient-to-t from-yellow-500 to-yellow-300 rounded-lg text-white text-xs font-bold hover:from-yellow-600 hover:to-yellow-400 shadow transition"
                              title="Send Payment Reminder"
                              onClick={() => handleSendReminder(inv)}
                              disabled={!!inv.paid || !!inv.cancelled}
                            >
                              Reminder
                            </button>
                            <button
                              className="px-3 py-1.5 bg-gradient-to-t from-green-500 to-green-300 rounded-lg text-white text-xs font-bold hover:from-green-700 hover:to-green-400 shadow transition"
                              title="Send Invoice by Email"
                              onClick={() => handleSendEmail(inv)}
                              disabled={!!inv.cancelled}
                            >
                              Email
                            </button>
                            <button
                              className="px-3 py-1.5 bg-gradient-to-t from-teal-500 to-teal-300 rounded-lg text-white text-xs font-bold hover:from-teal-700 hover:to-teal-400 shadow transition"
                              title="Send Invoice via WhatsApp"
                              onClick={() => handleSendWhatsapp(inv)}
                              disabled={!!inv.cancelled}
                            >
                              WhatsApp
                            </button>
                            <button
                              className={`px-3 py-1.5 bg-gradient-to-t from-gray-500 to-gray-300 rounded-lg text-white text-xs font-bold hover:from-gray-700 hover:to-gray-500 shadow transition ${
                                !!inv.cancelled
                                  ? "opacity-40 cursor-not-allowed"
                                  : ""
                              }`}
                              title="Cancel Invoice"
                              onClick={() =>
                                handleCancelInvoice(
                                  inv,
                                  fetchAllData,
                                  setErrorMsg,
                                  setUpdateLoadingId
                                )
                              }
                              disabled={!!inv.cancelled || !!inv.paid}
                            >
                              Cancel
                            </button>
                            <button
                              className="px-3 py-1.5 bg-gradient-to-t from-red-500 to-red-400 rounded-lg text-white text-xs font-bold hover:from-red-700 hover:to-red-500 shadow transition"
                              title="Delete Invoice"
                              onClick={() => handleDeleteInvoice(inv.id!)}
                              disabled={deleteLoadingId === inv.id}
                            >
                              {deleteLoadingId === inv.id ? "Deleting..." : "Delete"}
                            </button>
                          </>
                        )}
                      </div></td>
                      {/* Status rendered in main column */}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invoice view modal with full h-screen usage */}
      {viewingInvoice && (
        <div className="fixed inset-0 z-50 flex flex-col h-screen w-screen">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40"></div>
          {/* Modal */}
          <div className="relative flex flex-col flex-1 justify-center items-center h-full w-full py-7 px-2">
            <div className="bg-gradient-to-tr from-white to-blue-50 w-full max-w-[900px] h-full max-h-[97vh] flex flex-col overflow-hidden rounded-2xl shadow-2xl border border-blue-200">
              <div className="flex justify-end gap-5 py-3 px-4 flex-shrink-0 bg-gradient-to-r from-blue-200/20 via-blue-100/5 to-white rounded-t-2xl border-b border-blue-100">
                <button
                  onClick={() => handlePrint()}
                  className="bg-gradient-to-r from-blue-600 to-blue-400 text-white px-3 py-2 rounded-xl shadow hover:from-blue-400 hover:to-blue-200 transition text-[13px]"
                >
                  Print
                </button>
                <button
                  onClick={handleCloseModal}
                  className="bg-gray-500 hover:bg-gray-800 text-white px-3 py-2 rounded-xl shadow transition text-[13px]"
                >
                  Close
                </button>
              </div>
              {/* Modal Content ... unchanged ... */}
              <div
                id="invoice-print"
                className="p-2 text-[11.5px] text-gray-800 overflow-scroll"
              >
                {/* Tax Invoice Heading */}
                <div
                  className="text-center py-4"
                  style={{
                    fontSize: "22px",
                    fontWeight: 800,
                    letterSpacing: "1px",
                    color: "#1e3a8a",
                    paddingTop: "24px",
                    paddingBottom: "18px",
                  }}
                >
                  TAX INVOICE
                </div>
                <table className="w-full border border-blue-300 border-collapse rounded-2xl shadow mb-2">
                  <tbody>
                    <tr className="invoice__header">
                      <td className="border p-2 w-1/2 align-top">
                        <div
                          className="mb-1 text-xl text-blue-900 font-extrabold tracking-wide"
                          style={{
                            fontSize: "14.5px",
                            lineHeight: "1.2",
                          }}
                        >
                          {
                            companies.find(
                              (c) =>
                                String(c.id) ===
                                String(viewingInvoice.Company_id)
                            )?.company_name
                          }
                        </div>
                        <div
                          className="mb-1 text-gray-700"
                          style={{ fontSize: "11.5px" }}
                        >
                          <b>Address:</b>{" "}
                          {
                            companies.find(
                              (c) =>
                                String(c.id) ===
                                String(viewingInvoice.Company_id)
                            )?.address
                          }
                        </div>
                        <div
                          className="mb-1 text-gray-700"
                          style={{ fontSize: "11.5px" }}
                        >
                          <b>GSTIN:</b>{" "}
                          {
                            companies.find(
                              (c) =>
                                String(c.id) ===
                                String(viewingInvoice.Company_id)
                            )?.gst_number
                          }
                        </div>
                        <div
                          className="mb-1 text-gray-700"
                          style={{ fontSize: "11.5px" }}
                        >
                          <b>State:</b>{" "}
                          {
                            companies.find(
                              (c) =>
                                String(c.id) ===
                                String(viewingInvoice.Company_id)
                            )?.state
                          }
                        </div>
                        <div
                          className="mb-1 text-gray-700"
                          style={{ fontSize: "11.5px" }}
                        >
                          <b>CIN:</b>{" "}
                          {
                            companies.find(
                              (c) =>
                                String(c.id) ===
                                String(viewingInvoice.Company_id)
                            )?.cin
                          }
                        </div>
                        <div
                          className="mb-1 text-gray-700"
                          style={{ fontSize: "11.5px" }}
                        >
                          <b>Email:</b>{" "}
                          {
                            companies.find(
                              (c) =>
                                String(c.id) ===
                                String(viewingInvoice.Company_id)
                            )?.email
                          }
                        </div>
                      </td>
                      <td className="border p-2 align-top bg-gradient-to-t from-blue-50 to-blue-100 flex flex-row">
                        <div
                          className="mb-1 flex flex-col"
                          style={{ fontSize: "12px", minWidth: "135px" }}
                        >
                          <b>Invoice No:</b>{" "}
                          <span className="text-blue-700">
                            {viewingInvoice.InvoiceNo}
                          </span>
                        </div>
                        <div
                          className="mb-1 flex flex-col ml-8"
                          style={{ fontSize: "12px", minWidth: "110px" }}
                        >
                          <b>Dated:</b>{" "}
                          <span className="text-blue-700">
                            {viewingInvoice.Date ? formatDateToDDMMYYYY(viewingInvoice.Date) : ""}
                          </span>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="border p-2 align-top bg-gradient-to-l from-pink-50 to-white/90" style={{ fontSize: '11.5px' }}>
                        <b className="text-blue-800">Buyer (Bill to)</b>
                        <div className="" />
                        {(() => {
                          const customerObj = customers.find(
                            (c) => String(c.id) === String(viewingInvoice.Customer_id || (viewingInvoice as any).customer_id)
                          );
                          if (!customerObj) {
                            return <div className="text-gray-400 italic mt-1">Customer details not found</div>;
                          }
                          return (
                            <>
                              <div className="mb-1 mt-1">
                                <b>{customerObj.company_name}</b>
                              </div>
                              <div className="mb-1" style={{ whiteSpace: 'pre-line' }}>
                                <b>Address:</b> {customerObj.address || 'N/A'}
                              </div>
                              <div className="mb-1">
                                <b>City:</b> {customerObj.city || 'N/A'}
                              </div>
                              <div className="mb-1">
                                <b>State:</b> {customerObj.state || 'N/A'}
                              </div>
                              <div className="mb-1">
                                <b>GSTIN:</b> {customerObj.gst_number || 'N/A'}
                              </div>
                            </>
                          );
                        })()}
                      </td>
                      <td className="border p-2 bg-white align-top" style={{ fontSize: '11.5px' }}>
                        {viewingInvoice.shipToAddress && (
                          <>
                            <b className="text-blue-800">Ship To</b>
                            <div className="mt-1" style={{ whiteSpace: 'pre-line' }}>
                              {viewingInvoice.shipToAddress}
                            </div>
                          </>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
                {/* ...rest unchanged for brevity */}
                {/* Render safely from getPreviewRows */}
                <table
                  className="w-full border border-black border-collapse mt-[2px] mb-2 rounded-lg"
                  style={{
                    fontSize: "11.5px",
                    borderWidth: "1.2px",
                  }}
                >
                  <thead>
                    <tr style={{ height: "22px", fontSize: "11.7px" }}>
                      <th
                        className="border border-black"
                        style={{
                          padding: "3px 6px",
                          textAlign: "center",
                          fontWeight: 600,
                        }}
                      >
                        Particulars
                      </th>
                      <th
                        className="border border-black"
                        style={{
                          padding: "3px 6px",
                          textAlign: "center",
                          fontWeight: 600,
                          width: "90px",
                        }}
                      >
                        HSN/SAC
                      </th>
                      <th
                        className="border border-black"
                        style={{
                          padding: "3px 6px",
                          textAlign: "center",
                          fontWeight: 600,
                          width: "110px",
                        }}
                      >
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {getPreviewRows().map((r, i) => (
                      <tr key={i}>
                        <td
                          className="border border-black align-top"
                          style={{
                            padding: "7px 10px 24px 7px",
                            fontSize: "12.4px",
                            minHeight: "90px",
                          }}
                        >
                          <div>
                            <div style={{ whiteSpace: "pre-line" }}>
                              {r.particulars}
                            </div>
                            {i === 0 && (
                              <div
                                style={{
                                  marginTop: 24,
                                  fontWeight: 700,
                                  letterSpacing: 1,
                                  fontFamily: "inherit",
                                  fontSize: "12px",
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                {/* GST sublabels if needed */}
                              </div>
                            )}
                          </div>
                        </td>
                        <td
                          className="border border-black align-top"
                          style={{
                            padding: "7px 8px",
                            fontSize: "12.6px",
                            textAlign: "center",
                            minWidth: "64px",
                          }}
                        >
                          {r.hsn_sac}
                        </td>
                        <td
                          className="border border-black align-top text-right"
                          style={{
                            padding: "7px 8px",
                            fontSize: "12.6px",
                            fontFamily: "inherit",
                            minWidth: "84px",
                          }}
                        >
                          {parseFloat(r.amount).toLocaleString("en-IN")}
                        </td>
                      </tr>
                    ))}
                    {/* CGST/SGST/IGST Rows */}
                    {Number(invoicePreviewData.sgstAmount) > 0 && (
                      <tr>
                        <td
                          className="border-t-0 border-l border-b-0 border-black text-end"
                          style={{ padding: 0 }}
                        >
                          <span
                            style={{
                              marginLeft: "auto",
                              marginBottom: "-2px",
                              lineHeight: 1.33,
                              color: "#222",
                              fontWeight: 600,
                            }}
                          >
                            CGST (
                            {Number(invoicePreviewData.selectedGstRate) / 2}
                            %)
                          </span>
                        </td>
                        <td
                          className="border-t-0 border-b-0 border-black"
                          style={{ padding: 0 }}
                        ></td>
                        <td
                          className="border border-black text-right"
                          style={{
                            padding: "5.3px 8px",
                            fontSize: "12.7px",
                            fontWeight: 500,
                          }}
                        >
                          {Number(
                            invoicePreviewData.cgstAmount
                          ).toLocaleString("en-IN")}
                        </td>
                      </tr>
                    )}
                    {Number(invoicePreviewData.sgstAmount) > 0 && (
                      <tr>
                        <td
                          className="border-t-0 border-l border-b-0 border-black text-end"
                          style={{ padding: 0 }}
                        >
                          <span
                            style={{
                              marginLeft: "auto",
                              marginBottom: "-2px",
                              lineHeight: 1.33,
                              color: "#222",
                              fontWeight: 600,
                            }}
                          >
                            SGST(
                            {Number(invoicePreviewData.selectedGstRate) / 2}
                            %)
                          </span>
                        </td>
                        <td
                          className="border-t-0 border-b-0 border-black"
                          style={{ padding: 0 }}
                        ></td>
                        <td
                          className="border border-black text-right"
                          style={{
                            padding: "5.3px 8px",
                            fontSize: "12.7px",
                            fontWeight: 500,
                          }}
                        >
                          {Number(
                            invoicePreviewData.sgstAmount
                          ).toLocaleString("en-IN")}
                        </td>
                      </tr>
                    )}
                    {Number(invoicePreviewData.igstAmount) > 0 && (
                      <tr>
                        <td
                          className="border-t-0 border-l border-b-0 border-black text-end"
                          style={{ padding: 0 }}
                        >
                          <span
                            style={{
                              marginLeft: "auto",
                              marginBottom: "-2px",
                              lineHeight: 1.33,
                              color: "#222",
                              fontWeight: 600,
                            }}
                          >
                            IGST(
                            {Number(invoicePreviewData.selectedGstRate)}%)
                          </span>
                        </td>
                        <td
                          className="border-t-0 border-b-0 border-black"
                          style={{ padding: 0 }}
                        ></td>
                        <td
                          className="border border-black text-right"
                          style={{
                            padding: "5.3px 8px",
                            fontSize: "12.7px",
                            fontWeight: 500,
                          }}
                        >
                          {Number(
                            invoicePreviewData.igstAmount
                          ).toLocaleString("en-IN")}
                        </td>
                      </tr>
                    )}
                    <tr className="font-bold bg-green-50 invoice-total">
                      <td className="border p-2 text-right" colSpan={2}>
                        Total
                      </td>
                      <td className="border p-2 text-right text-green-700 text-[16px] font-bold">
                        ₹ {invoicePreviewData.totalAmount}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="mt-2 mb-1">
                  {/* Amount in Words */}
                  <div className="w-full border border-gray-200 rounded-lg bg-gray-50 px-2 py-2 mb-2 flex flex-col">
                    <span
                      className="text-lg font-bold text-blue-900 mb-1"
                      style={{ fontSize: "12.5px" }}
                    >
                      Amount Chargeable (in words)
                    </span>
                    <span className="text-[11.5px] text-gray-700 font-mono">
                      {amountInWords} /-
                    </span>
                  </div>
                  {/* Tax Details Table */}
                  <div className="w-full border border-blue-200 rounded-xl bg-white px-0 sm:px-1 py-2 mb-2 shadow-lg">
                    <div className="flex items-center border-b border-blue-100 pb-1 px-2">
                      <span
                        className="text-blue-900 text-lg font-semibold tracking-wide"
                        style={{ fontSize: "12.2px" }}
                      >
                        Tax Details
                      </span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse mt-1">
                        <thead>
                          <tr className="bg-blue-50 text-blue-900 text-[11px]">
                            <th className="border border-blue-200 px-2 py-1 text-left font-semibold align-middle">
                              HSN/SAC
                            </th>
                            <th className="border border-blue-200 px-2 py-1 text-right font-semibold align-middle">
                              Taxable Value (₹)
                            </th>
                            <th className="border border-blue-200 px-1 py-0" colSpan={2}>
                              <div className="flex flex-col text-center">
                                <span className="font-semibold" style={{ fontSize: "11px" }}>
                                  IGST
                                </span>
                                <div className="flex justify-between w-full border-t border-blue-100 mt-1">
                                  <span className="flex-1 border-r border-blue-100 py-1 text-xs font-normal" style={{ fontSize: "9.5px" }}>
                                    Rate (%)
                                  </span>
                                  <span className="flex-1 py-1 text-xs font-normal" style={{ fontSize: "9.5px" }}>
                                    Amount (₹)
                                  </span>
                                </div>
                              </div>
                            </th>
                            <th className="border border-blue-200 px-1 py-0" colSpan={2}>
                              <div className="flex flex-col text-center">
                                <span className="font-semibold" style={{ fontSize: "11px" }}>
                                  SGST
                                </span>
                                <div className="flex justify-between  w-full border-t border-blue-100 mt-1">
                                  <span className="flex-1 border-r border-blue-100 py-1 text-xs font-normal" style={{ fontSize: "9.5px" }}>
                                    Rate (%)
                                  </span>
                                  <span className="flex-1 py-1 text-xs font-normal" style={{ fontSize: "9.5px" }}>
                                    Amount (₹)
                                  </span>
                                </div>
                              </div>
                            </th>
                            <th className="border border-blue-200 px-1 py-0" colSpan={2}>
                              <div className="flex flex-col text-center">
                                <span className="font-semibold" style={{ fontSize: "11px" }}>
                                  CGST
                                </span>
                                <div className="flex justify-between w-full border-t border-blue-100 mt-1">
                                  <span className="flex-1 border-r border-blue-100 py-1 text-xs font-normal" style={{ fontSize: "9.5px" }}>
                                    Rate (%)
                                  </span>
                                  <span className="flex-1 py-1 text-xs font-normal" style={{ fontSize: "9.5px" }}>
                                    Amount (₹)
                                  </span>
                                </div>
                              </div>
                            </th>
                            <th className="border border-blue-200 px-2 py-1 text-right font-semibold align-middle">
                              Total Tax
                              <br />
                              Amount (₹)
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const { taxRows } = taxTableData;
                            if (!taxRows.length) {
                              // Demo Row
                              return (
                                <tr className="even:bg-blue-50 text-[11px]">
                                  <td className="border border-blue-100 px-2 py-1 align-middle"></td>
                                  <td className="border border-blue-100 px-2 py-1 text-right align-middle"></td>
                                  <td className="border border-blue-100 px-1 py-1 text-center align-middle"></td>
                                  <td className="border border-blue-100 px-1 py-1 text-right align-middle"></td>
                                  <td className="border border-blue-100 px-1 py-1 text-center align-middle"></td>
                                  <td className="border border-blue-100 px-1 py-1 text-right align-middle"></td>
                                  <td className="border border-blue-100 px-1 py-1 text-center align-middle"></td>
                                  <td className="border border-blue-100 px-1 py-1 text-right align-middle"></td>
                                  <td className="border border-blue-100 px-2 py-1 text-right align-middle font-semibold"></td>
                                </tr>
                              );
                            }
                            return taxRows.map((row, idx) => (
                              <tr key={row.hsn + idx} className="even:bg-blue-50 text-[11px]">
                                {/* HSN */}
                                <td className="border border-blue-100 px-2 py-1 align-middle">{row.hsn}</td>
                                {/* Taxable Value */}
                                <td className="border border-blue-100 px-2 py-1 text-right align-middle">
                                  {row.taxableValue}
                                </td>
                                {/* IGST Rate and Amount */}
                                <td className="border border-blue-100 px-1 py-1 text-center align-middle">
                                  {row.igstRate && row.igstAmount ? `${row.igstRate}%` : "-"}
                                </td>
                                <td className="border border-blue-100 px-1 py-1 text-right align-middle">
                                  {row.igstAmount ? row.igstAmount : "-"}
                                </td>
                                {/* SGST/UTGST Rate and Amount */}
                                <td className="border border-blue-100 px-1 py-1 text-center align-middle">
                                  {row.sgstRate && row.sgstAmount
                                    ? `${row.sgstRate}%`
                                    : "-"}
                                </td>
                                <td className="border border-blue-100 px-1 py-1 text-right align-middle">
                                  {row.sgstAmount ? row.sgstAmount : "-"}
                                </td>
                                <td className="border border-blue-100 px-1 py-1 text-center align-middle">
                                  {row.cgstRate && row.cgstAmount
                                    ? `${row.cgstRate}%`
                                    : "-"}
                                </td>
                                <td className="border border-blue-100 px-1 py-1 text-right align-middle">
                                  {row.cgstAmount ? row.cgstAmount : "-"}
                                </td>
                                {/* Total Tax */}
                                <td className="border border-blue-100 px-2 py-1 text-right align-middle font-semibold">
                                  {row.totalTax}
                                </td>
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {/* Tax Amount (in words) - dynamic conversion */}
                  <div className="w-full border border-gray-200 rounded-lg bg-gray-50 px-2 py-2 my-2 flex flex-col">
                    <span
                      className="text-lg font-bold text-blue-900 mb-1"
                      style={{ fontSize: "12.5px" }}
                    >
                      Tax Amount (in words)
                    </span>
                    <span className="text-[11.5px] text-gray-700 font-mono">
                      {taxAmountInWords} /-
                    </span>
                  </div>
                </div>
                {/* BANK DETAILS */}
                <div className="w-full border border-blue-200 border-collapse mt-[4px] rounded-lg overflow-hidden flex flex-row items-stretch bg-white">
                  <div
                    className="w-1/2 border-r border-blue-200 p-2 flex flex-col justify-between bg-gray-50"
                    style={{ fontSize: "11px" }}
                  >
                    {/* REMARKS SECTION */}
                    {viewingInvoice.remarks && (
                      <div className="mb-1 mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md text-gray-800">
                        <b className="text-blue-900">Remarks:</b>
                        <div className="mt-1" style={{ whiteSpace: "pre-line" }}>
                          {viewingInvoice.remarks}
                        </div>
                      </div>
                    )}
                    {/* END REMARKS */}
                    <div>
                      <b className="text-blue-900">Company's PAN: </b>
                      <span className="font-semibold text-gray-800">
                        {companies.find((c) => String(c.id) === String(viewingInvoice.Company_id))?.pancard || "N/A"}
                      </span>
                    </div>
                    <div className="mt-2">
                      <b className="text-blue-900 mb-1 block">Declaration</b>
                      <div className="text-gray-800 mt-1">
                        We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.
                      </div>
                    </div>
                  </div>
                  <div className="w-1/2 p-2 flex flex-row justify-between text-left bg-white">
                    <div className="flex flex-col w-full border border-blue-200 border-collapse mt-1 rounded-lg overflow-hidden">
                      <div className="border p-2 w-full align-top bg-gray-50 rounded-bl-lg flex flex-col" style={{ fontSize: '11px' }}>
                        <b className="text-blue-900">Bank Details</b>
                        {viewingBankDetails ? (
                          <>
                            <div className="mt-1 mb-1">A/c Holder Name :{" "}
                              {viewingBankDetails.account_holder_name ||
                                companies.find((c) => String(c.id) === String(viewingInvoice.Company_id))?.company_name}
                            </div>
                            <div>Bank Name : <span className="font-semibold">{viewingBankDetails.bank_name}</span></div>
                            <div>A/c No : <span className="font-semibold">{viewingBankDetails.account_number}</span></div>
                            <div>Branch : <span className="font-semibold">{viewingBankDetails.branch || 'N/A'}</span></div>
                            {viewingBankDetails.ifsc_code && (
                              <div>IFSC : <span className="font-semibold">{viewingBankDetails.ifsc_code}</span></div>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="mt-1 mb-1">A/c Holder Name :{" "}
                              {companies.find((c) => String(c.id) === String(viewingInvoice.Company_id))?.company_name}
                            </div>
                            <div className="text-gray-400 italic">No bank details on file</div>
                          </>
                        )}
                      </div>
                      <div className="border border-l-0 p-2 w-full text-right align-top bg-white rounded-br-lg flex flex-col justify-between">
                        <div>
                          <div className="mb-2">For <b>
                            {
                              companies.find((c) => String(c.id) === String(viewingInvoice.Company_id))?.company_name
                            }
                          </b></div>
                          {(() => {
                            const companyObj = companies.find(
                              (c) => String(c.id) === String(viewingInvoice.Company_id)
                            );
                            const signatureUrl = getCompanySignatureUrl(companyObj);
                            if (
                              companyObj &&
                              typeof companyObj.signature === "string" &&
                              companyObj.signature.trim() !== "" &&
                              signatureUrl
                            ) {
                              return (
                                <img
                                  src={signatureUrl}
                                  alt="Authorised Signature"
                                  className="h-12 w-auto object-contain ml-auto"
                                  style={{ maxWidth: "120px" }}
                                  onError={e => { e.currentTarget.style.display = "none"; }}
                                />
                              );
                            }
                            return (
                              <div className="text-gray-400 italic text-sm text-center" style={{ fontSize: '10px' }}>
                                No signature on file
                              </div>
                            );
                          })()}
                          <div className="pt-1 font-semibold" style={{ fontSize: '10.5px' }}>Authorised Signatory</div>
                        </div>
                      </div>
                    </div>
              </div>
              </div>
              </div>
              {/* Linked Bank Transactions Display (only visible to admin in modal, not printed) */}
              {viewingInvoice && (() => {
                const linkedRows = statementRows.filter(r => 
                  (r.mapped_invoice_id && Number(r.mapped_invoice_id) === Number(viewingInvoice.id)) ||
                  (r.mapped_invoice_ids && String(r.mapped_invoice_ids).split(',').map(id => id.trim()).includes(String(viewingInvoice.id)))
                );
                
                if (linkedRows.length === 0) return null;
                
                return (
                  <div className="mx-6 my-4 p-4 bg-blue-50/50 border border-blue-200 rounded-2xl flex-shrink-0">
                    <h4 className="text-xs font-black text-blue-900 uppercase tracking-widest mb-2.5 flex items-center gap-1.5">
                      🔗 Linked Bank Statement Transactions
                    </h4>
                    <div className="space-y-2 max-h-28 overflow-y-auto pr-1">
                      {linkedRows.map(row => (
                        <div key={row.id} className="flex justify-between items-center text-xs p-2 bg-white border border-blue-100 rounded-xl shadow-sm">
                          <div className="flex flex-col text-left">
                            <span className="font-semibold text-slate-700">
                              {row.date_str ? row.date_str.substring(0, 10) : ''} | {row.particulars}
                            </span>
                            {row.remarks && (
                              <span className="text-[10px] text-gray-500 italic mt-0.5">
                                Remarks: {row.remarks}
                              </span>
                            )}
                          </div>
                          <div className="text-right flex flex-col items-end">
                            <span className="font-extrabold text-blue-900">
                              ₹ {Number(row.credit).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </span>
                            {row.tds_amount > 0 && (
                              <span className="text-[10px] text-amber-700 font-bold mt-0.5">
                                TDS: ₹ {Number(row.tds_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </span>
                            )}
                            {row.gateway_deduction > 0 && (
                              <span className="text-[10px] text-purple-700 font-bold mt-0.5">
                                Gateway Fee: ₹ {Number(row.gateway_deduction).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      {/* Share Notification Modal */}
      {shareModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-xl w-full shadow-2xl border border-blue-50 overflow-hidden animate-fade-in">
            <div className="bg-gradient-to-r from-blue-700 to-blue-600 px-6 py-4 text-white flex justify-between items-center">
              <h3 className="text-lg font-bold">Share Invoice details</h3>
              <button 
                onClick={() => setShareModalOpen(false)}
                className="text-white hover:text-gray-200 font-bold focus:outline-none text-xl"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4 text-left">
              {/* Channel Selector */}
              <div>
                <label className="block text-xs font-semibold uppercase text-blue-900/80 mb-2">Communication Channel</label>
                <div className="flex gap-2">
                  {[
                    { id: 'email', label: '✉️ Email', color: 'bg-green-50 text-green-700 border-green-200' },
                    { id: 'sms', label: '💬 SMS', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
                    { id: 'whatsapp', label: '🟢 WhatsApp', color: 'bg-teal-50 text-teal-700 border-teal-200' }
                  ].map(ch => (
                    <button
                      key={ch.id}
                      type="button"
                      onClick={() => {
                        setShareChannel(ch.id as any);
                        if (shareInvoice) {
                          const cust = customers.find(c => String(c.id) === String(shareInvoice.Customer_id));
                          if (ch.id === 'email') {
                            setShareRecipient(cust && cust.email ? cust.email : '');
                          } else {
                            setShareRecipient(cust && cust.number ? String(cust.number) : (cust && cust.phone ? String(cust.phone) : ''));
                          }
                          const custName = cust ? cust.company_name : 'Customer';
                          const comp = companies.find(c => String(c.id) === String(shareInvoice.Company_id));
                          const compName = comp ? comp.company_name : 'Our Company';
                          if (ch.id === 'sms') {
                            setShareMessageText(`Dear ${custName}, invoice ${shareInvoice.InvoiceNo} of ₹ ${shareInvoice.Total_Amount} is due. Please pay. - ${compName}`);
                          } else if (ch.id === 'whatsapp') {
                            setShareMessageText(`Hello *${custName}*,\n\nHere is your invoice detail:\n*Invoice No:* ${shareInvoice.InvoiceNo}\n*Date:* ${new Date(shareInvoice.Date).toLocaleDateString('en-GB')}\n*Total Amount:* ₹ ${shareInvoice.Total_Amount}\n\nThank you for your business!\n\nRegards,\n*${compName}*`);
                          } else {
                            setShareMessageText(`Hello ${custName},\n\nHere are the details for invoice ${shareInvoice.InvoiceNo} dated ${new Date(shareInvoice.Date).toLocaleDateString('en-GB')}:\nTotal Amount: ₹ ${shareInvoice.Total_Amount}\n\nThank you for your business.\n\nRegards,\n${compName}`);
                          }
                        }
                      }}
                      className={`flex-1 py-2 px-3 text-xs font-bold border rounded-xl transition ${
                        shareChannel === ch.id 
                          ? 'border-blue-600 text-blue-700 bg-blue-50/50 ring-2 ring-blue-100'
                          : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {ch.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Recipient Input */}
              <div>
                <label className="block text-xs font-semibold uppercase text-blue-900/80 mb-1">
                  {shareChannel === 'email' ? 'Recipient Email Address' : 'Recipient Phone Number'}
                </label>
                <input
                  type={shareChannel === 'email' ? 'email' : 'text'}
                  value={shareRecipient}
                  onChange={e => setShareRecipient(e.target.value)}
                  className="w-full border-2 border-blue-50 rounded-xl px-4 py-2 text-sm focus:border-blue-300 focus:outline-none"
                  placeholder={shareChannel === 'email' ? 'e.g. client@email.com' : 'e.g. 919876543210'}
                />
              </div>

              {/* Subject Input (Email Only) */}
              {shareChannel === 'email' && (
                <div>
                  <label className="block text-xs font-semibold uppercase text-blue-900/80 mb-1">Email Subject</label>
                  <input
                    type="text"
                    value={shareSubject}
                    onChange={e => setShareSubject(e.target.value)}
                    className="w-full border-2 border-blue-50 rounded-xl px-4 py-2 text-sm focus:border-blue-300 focus:outline-none"
                    placeholder="Subject"
                  />
                </div>
              )}

              {/* Message Input */}
              <div>
                <label className="block text-xs font-semibold uppercase text-blue-900/80 mb-1">Message Body</label>
                <textarea
                  value={shareMessageText}
                  onChange={e => setShareMessageText(e.target.value)}
                  rows={5}
                  className="w-full border-2 border-blue-50 rounded-xl px-4 py-2 text-sm focus:border-blue-300 focus:outline-none font-mono"
                  placeholder="Enter message text"
                />
              </div>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setShareModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-gray-500 hover:text-gray-700 bg-white border rounded-xl hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExecuteShare}
                disabled={shareSending}
                className="px-5 py-2 text-xs font-bold text-white bg-blue-600 rounded-xl hover:bg-blue-700 shadow-md transition disabled:opacity-50"
              >
                {shareSending ? 'Sending...' : 'Send Now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bank Statement Mapping Modal */}
      {bankMapModalOpen && selectedInvoiceForMap && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setBankMapModalOpen(false)}></div>
          <div className="relative bg-white w-full max-w-[500px] rounded-2xl shadow-2xl border border-blue-200 overflow-hidden m-4">
            <div className="bg-gradient-to-r from-blue-600 to-blue-400 text-white p-4 font-bold">
              Link Bank Statement Row: {selectedInvoiceForMap.InvoiceNo}
            </div>
            <div className="p-6 flex flex-col gap-4">
              <div>
                <label className="block text-xs font-semibold text-blue-900/80 mb-2 uppercase tracking-wide">
                  Select Bank Transaction
                </label>
                <select
                  value={selectedStatementRowId}
                  onChange={e => setSelectedStatementRowId(e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-4 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800 font-semibold"
                >
                  <option value="">-- Select Statement Row (Optional) --</option>
                  {statementRows
                    .filter(r => r.credit > 0 && !r.mapped_invoice_id && !r.mapped_purchase_id)
                    .map(r => (
                      <option key={r.id} value={r.id}>
                        {r.date_str ? r.date_str.substring(0, 10) : ''} | {r.particulars} | Credit: ₹{r.credit}
                      </option>
                    ))}
                </select>
                {statementRows.filter(r => r.credit > 0 && !r.mapped_invoice_id && !r.mapped_purchase_id).length === 0 && (
                  <span className="text-xs text-amber-600 font-semibold mt-1 block">
                    No unmapped bank statement credit rows found.
                  </span>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-blue-900/80 mb-2 uppercase tracking-wide">
                  TDS Deduction Amount (Optional, ₹)
                </label>
                <input
                  type="number"
                  value={tdsAmount}
                  onChange={e => setTdsAmount(e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setBankMapModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-gray-500 hover:text-gray-700 bg-white border rounded-xl hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const amt = selectedMapStatus === 2 ? parseFloat(window.prompt("Enter the amount paid (₹):", String(selectedInvoiceForMap.amount_paid || "")) || "0") : undefined;
                  if (selectedMapStatus === 2 && (amt === undefined || isNaN(amt) || amt < 0)) {
                    alert("Invalid amount.");
                    return;
                  }
                  setBankMapModalOpen(false);
                  await handlePaidStatusUpdate(selectedInvoiceForMap.id!, selectedMapStatus, amt, 'bank');
                }}
                className="px-4 py-2 text-xs font-semibold text-amber-700 hover:text-amber-800 bg-white border border-amber-200 rounded-xl hover:bg-amber-50 transition"
              >
                Skip Link
              </button>
              <button
                type="button"
                disabled={!selectedStatementRowId}
                onClick={async () => {
                  try {
                    await axios.post(`${API_URL}/auth/bank-statement-row/${selectedStatementRowId}/map`, {
                      mapped_invoice_id: selectedInvoiceForMap.id,
                      tds_amount: parseFloat(tdsAmount || "0"),
                      is_partial: selectedMapStatus === 2
                    });
                    setBankMapModalOpen(false);
                    await fetchAllData();
                  } catch (err: any) {
                    alert(err.response?.data?.error || "Failed to link statement row.");
                  }
                }}
                className="px-5 py-2 text-xs font-bold text-white bg-blue-600 rounded-xl hover:bg-blue-700 shadow-md transition disabled:opacity-50"
              >
                Link & Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoice;